import { getWindowSessionReplayURL } from './utils';

export const sendBWEvent = (eventName: string, eventData?: Record<string, any>): void => {
  // Safety check: prevent BrightWrite calls in headless mode or when bwtag is not available
  if (!window || window.isHeadless || !window.bwtag || typeof window.bwtag !== 'function') {
    return;
  }

  try {
    window.bwtag('record', eventName, eventData);
  } catch (error) {
    // Silently fail to prevent breaking the application
    console.warn('BrightWrite tracking failed:', error);
  }
};

export const track = (argument: any) => {
  if (typeof window.dataLayer !== 'object') {
    return;
  }
  if (typeof argument === 'string') {
    window.dataLayer.push({
      event: argument,
    });
  } else {
    window.dataLayer.push({
      ...argument,
    });
  }
};

export const bwClickTracking = (event: any, customVariables = {}) => {
  if (!window || window.isHeadless || !window.bwtag || typeof window.bwtag !== 'function') {
    return;
  }

  try {
    const { dataset } = event.currentTarget;
    window.bwtag('record', 'click', { analyticsID: dataset?.analytics, ...customVariables }, event);
  } catch (error) {
    console.warn('BrightWrite click tracking failed:', error);
  }
};

export const bwNPSScoreTracking = ({
  score,
  source,
  bookingID,
  feedback,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  policyTypeGroupName,
  claimReference,
}: {
  score: string;
  source: string;
  bookingID?: string;
  feedback?: string;
  partnerID?: string;
  partnerName?: string;
  subsidiaryID?: string;
  subsidiaryName?: string;
  policyTypeGroupName?: string;
  claimReference?: string;
}) => {
  if (!window || window.isHeadless || !window.bwtag || typeof window.bwtag !== 'function') {
    return;
  }

  try {
    window.bwtag('record', 'nps_widget:score', {
      score,
      feedback: feedback ?? null,
      source,
      bookingID: bookingID ?? null,
      ins: bookingID,
      bwSession: getWindowSessionReplayURL() || 'NA',
      partnerID,
      partnerName,
      subsidiaryID,
      subsidiaryName,
      policyTypeGroupName,
      claimReference,
      claim_cla: claimReference,
    });
  } catch (error) {
    console.warn('BrightWrite NPS tracking failed:', error);
  }
};

interface PaymentSurveyTrackingArgs {
  payoutID?: string | undefined;
  preferredPaymentMethods: string[];
  acceptInstantPaymentMethodWithFee: string;
  preferredInstantPaymentMethod?: string;
  score: number;
}

export const bwPaymentSurveyTracking = ({
  payoutID,
  preferredPaymentMethods,
  acceptInstantPaymentMethodWithFee,
  preferredInstantPaymentMethod,
  score,
}: PaymentSurveyTrackingArgs) => {
  if (!window || window.isHeadless || !window.bwtag || typeof window.bwtag !== 'function') {
    return;
  }

  try {
    window.bwtag('record', 'paymentSurvey', {
      payoutID,
      preferredPaymentMethods,
      acceptInstantPaymentMethodWithFee,
      preferredInstantPaymentMethod,
      score,
      bwSession: getWindowSessionReplayURL() || 'NA',
    });
  } catch (error) {
    console.warn('BrightWrite payment survey tracking failed:', error);
  }
};

interface IBWContactTrackingArgs {
  source: string;
  contactReason?: string[];
  bookingID?: string;
  referer?: string | null;
  claimReference?: string | null;
  request?: string;
  partnerID?: string;
  partnerName?: string;
  subsidiaryID?: string;
  subsidiaryName?: string;
}

export const bwContactTracking = ({
  source,
  contactReason,
  bookingID,
  referer,
  claimReference,
  partnerID,
  partnerName,
  subsidiaryID,
  subsidiaryName,
  request,
}: IBWContactTrackingArgs) => {
  if (!window || window.isHeadless || !window.bwtag || typeof window.bwtag !== 'function') {
    return;
  }

  try {
    window.bwtag('record', 'contact-us', {
      source,
      contactReason: contactReason ?? null,
      bookingReference: bookingID ?? null,
      ins: bookingID ?? null,
      referer: referer ?? null,
      claimReference: claimReference ?? null,
      claim_cla: claimReference ?? null,
      request: request ?? null,
      partnerID,
      partnerName,
      subsidiaryID,
      subsidiaryName,
    });
  } catch (error) {
    console.warn('BrightWrite contact tracking failed:', error);
  }
};

export async function getBWVariant(experimentAPIName: string): Promise<{ apiName: string } | undefined> {
  return new Promise((resolve) => {
    // Safety check for headless mode and BrightWrite availability
    if (typeof window === 'undefined' || window.isHeadless || !window.bwtag) {
      resolve(undefined);
      return;
    }

    try {
      window.bwtag('retrieveExperiment', experimentAPIName, (experiment: { getVariant: () => void }) => {
        const variant: any = experiment?.getVariant();
        return resolve(variant);
      });
    } catch (error) {
      console.warn('Failed to retrieve BrightWrite experiment variant:', error);
      resolve(undefined);
    }
  });
}
